import { useQuery } from '@tanstack/react-query';
import { legacy } from '@/shared/api/client';
import { quantityLabel } from '@/shared/lib/passbook-utils';
import type { Passbook } from '@/shared/types';

export interface DashboardSummary {
  fidelized: number;
  purchases: number;
  revenue: number;
  avgTicket: number;
  redemptions: number;
  /** Selos/pontos/cashback creditados (ou enviados). */
  pointsCredited: number;
  /** Cartelas completadas (selos) ou pontos/cashback resgatados. */
  pointsRedeemed: number;
  redemptionRate: number;
  averageAge: number;
  creditedLabel: string;
  redeemedLabel: string;
  redemptionRateHint: string;
}

export interface IntervalSeries {
  labels: string[];
  data: number[];
  totalPeriod: number;
}

interface SignedUpResponse {
  serialNumbers?: number;
}

interface TicketMedioResponse {
  quantidade?: number;
  total?: number;
}

interface ResgateResponse {
  total?: number;
}

function passbookHeaders(idPassbook: string) {
  return { headers: { 'X-Passbook-Id': idPassbook } };
}

function asNumber(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function parseInterval(res: {
  labels?: string[];
  data?: Array<string | number>;
  totalPeriod?: string | number;
}): IntervalSeries {
  return {
    labels: res.labels ?? [],
    data: (res.data ?? []).map((v) => Number(v) || 0),
    totalPeriod: Number(res.totalPeriod ?? 0),
  };
}

/**
 * KPIs da visão consolidada — mesmas fontes do legado (dash.js / dashboard_v2_storecard):
 * signed_up_user, ticketmedio, resgate, stamps|send_points, rewards, mediaIdade.
 */
async function fetchConsolidatedSummary(idPassbook: string, passbook: Passbook): Promise<DashboardSummary> {
  const headers = passbookHeaders(idPassbook);
  const label = quantityLabel(passbook);
  const isCashback = Boolean(passbook.hasCashback);
  const isPoints = Boolean(passbook.hasPoints) || isCashback;
  const isStamps = !isPoints && !passbook.hasTransaction;

  const creditedPath = isStamps ? `/stamps/${idPassbook}` : `/send_points/${idPassbook}`;

  const [signedUp, ticket, resgate, credited, rewards, averageAge] = await Promise.all([
    legacy
      .get<SignedUpResponse>(`/signed_up_user/${idPassbook}`, headers)
      .catch((): SignedUpResponse => ({})),
    legacy
      .get<TicketMedioResponse>(`/ticketmedio/${idPassbook}/`, headers)
      .catch((): TicketMedioResponse => ({})),
    legacy.get<ResgateResponse>(`/resgate/${idPassbook}/`, headers).catch((): ResgateResponse => ({})),
    legacy.get<unknown>(creditedPath, headers).catch(() => 0),
    isStamps
      ? legacy.get<unknown>(`/rewards/${idPassbook}/false`, headers).catch(() => 0)
      : Promise.resolve(0),
    legacy.get<unknown>(`/mediaIdade/${idPassbook}`, headers).catch(() => 0),
  ]);

  const purchases = asNumber(ticket?.quantidade);
  const revenue = asNumber(ticket?.total);
  const avgTicket = purchases > 0 ? revenue / purchases : 0;
  const redemptions = asNumber(resgate?.total);
  const pointsCredited = asNumber(credited);
  const completedCards = asNumber(rewards);
  const pointsRedeemed = isStamps ? completedCards : redemptions;
  const rateBase = isStamps ? completedCards : pointsCredited;
  const redemptionRate = rateBase > 0 ? (redemptions / rateBase) * 100 : 0;

  return {
    fidelized: asNumber(signedUp?.serialNumbers),
    purchases,
    revenue,
    avgTicket,
    redemptions,
    pointsCredited,
    pointsRedeemed,
    redemptionRate,
    averageAge: asNumber(averageAge),
    creditedLabel: isCashback
      ? 'Cashback creditado'
      : isPoints
        ? 'Pontos creditados'
        : isStamps
          ? 'Selos enviados'
          : `${label} enviados`,
    redeemedLabel: isStamps
      ? 'Cartelas completadas'
      : isCashback
        ? 'Cashback resgatado'
        : isPoints
          ? 'Pontos resgatados'
          : 'Resgatados',
    redemptionRateHint: isStamps
      ? 'Percentual de resgate sobre cartelas completadas.'
      : `Percentual de resgate sobre ${label.toLowerCase()} creditados.`,
  };
}

/** Resumo consolidado (histórico completo — alinhado ao Metabase/ticketmedio). */
export function useDashboardSummary(idPassbook: string, passbook: Passbook, enabled = true) {
  return useQuery({
    queryKey: [
      'dashboard',
      'summary',
      idPassbook,
      passbook.hasPoints,
      passbook.hasCashback,
      passbook.hasTransaction,
    ],
    enabled,
    queryFn: () => fetchConsolidatedSummary(idPassbook, passbook),
    staleTime: 60_000,
  });
}

export function useSignupInterval(idPassbook: string, days = 7, enabled = true) {
  return useQuery({
    queryKey: ['dashboard', 'signup-interval', idPassbook, days],
    enabled,
    queryFn: async (): Promise<IntervalSeries> => {
      const res = await legacy.get<{ labels?: string[]; data?: string[]; totalPeriod?: number }>(
        `/user/by_interval_add/${idPassbook}/${days}`,
        passbookHeaders(idPassbook),
      );
      return parseInterval(res);
    },
    staleTime: 60_000,
  });
}
